import { Box, Button, Checkbox, Dialog, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'

import { useOnlineSessionsStore } from '../../../store/onlinesessions.store'
import { Icons, secondsToHMS, sortArrayOfObjectsByKey } from '../../../utils/utilities'
import noDataImg from '../../../assets/no-data.svg'
import CustomChip from '../../common/CustomChip'
import TableHeader from '../../common/TableHeader'
import { useParams } from 'react-router-dom'

const stickyCellShadow = {
    content: `''`,
    width: "4px",
    borderRadius: "2px 0 0 2px",
    background: `linear-gradient(to left, rgba(0, 0, 0, 0.15), transparent)`,
    position: "absolute",
    right: "100%",
    top: "0",
    bottom: "0"
}

const columns = [
    { name: "SL. NO.", minWidth: "90px", align: "center", padding: "10px 20px" },
    { name: "User ID", minWidth: "100px", padding: "10px 20px" },
    { name: "Name", minWidth: "250px", padding: "10px 20px" },
    { name: "Attendance", minWidth: "250px", padding: "10px 20px" },
    {
        name: "Present Status", minWidth: "160px", align: "center", padding: "10px 20px", styles: {
            position: "sticky", right: "-1px", bgcolor: "inherit",
            "&::before": stickyCellShadow
        }
    },
]


const AttendanceTableRow = ( { sessionAttendance = true, user, threshold, index, setPresentUsersList, isPresent } ) => {

    const { palette, border } = useTheme()

    const OnlineSessionsStore = useOnlineSessionsStore()

    const quizParticipants = OnlineSessionsStore.getQuizParticipationAttendance
    const viewersList = OnlineSessionsStore.getVideosWatchAttendance
    const meeting = OnlineSessionsStore.getSessionMetadata

    const didUserWatchVideos = viewersList?.find( u => u.user_id === user.user_id )

    const togglePresentStatus = ( event ) => {
        const present = !event.target.checked
        setPresentUsersList( prev => {
            const newList = [...prev]
            if ( present ) {
                const userIdIndex = newList.findIndex( userId => userId === user.user_id )
                newList.splice( userIdIndex, 1 )
            } else {
                newList.push( user.user_id )
            }
            return newList
        } )
    }

    return (
        <TableRow key={user.user_id} sx={{ bgcolor: palette.common.bg }} >
            <TableCell align="center" sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                {index}
            </TableCell>
            <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                {user?.user_id}
            </TableCell>
            <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                {user.name}
            </TableCell>

            {meeting.meeting_type === "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                    <CustomChip startIcon={sessionAttendance ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value={`Stream: ${sessionAttendance ? `${secondsToHMS( user.total_time / 1000 )} (${user.present_percentage}%)` : "00:00 (0%)"}`} />
                    <CustomChip startIcon={didUserWatchVideos ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value={`Video: ${didUserWatchVideos?.average || 0}%`} />
                    <CustomChip startIcon={quizParticipants.includes( user.user_id ) ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value="Quiz" />
                </Box>
            </TableCell>}

            <TableCell
                sx={{
                    padding: "5px 20px",
                    borderBottom: border[1],
                    position: "sticky",
                    right: "-1px",
                    background: "inherit",
                    "&::before": stickyCellShadow
                }}
                align="center"
            >
                {/* <CustomChip variant='filled' color={( user.present || didUserWatchVideos?.average >= threshold ) ? "success" : "error"} value={( user.present || didUserWatchVideos?.average >= threshold ) ? "PRESENT" : "ABSENT"} /> */}
                <Checkbox size='small' onChange={togglePresentStatus} checked={isPresent} />
            </TableCell>
        </TableRow>

    )
}

const HybridClassAttendanceConfirmDialog = observer( ( { open = false, handleClose } ) => {

    const [presentUsersList, setPresentUsersList] = useState( [] )

    const { palette, border, layout } = useTheme()
    const { meeting_id } = useParams()

    const OnlineSessionsStore = useOnlineSessionsStore()

    const videoWatchAttendance = OnlineSessionsStore.getVideosWatchAttendance
    const creatorAttendanceData = OnlineSessionsStore.getCreatorAttendance
    const streamAttendance = OnlineSessionsStore.getStreamAttendance

    const calculatedAttendance = useMemo( () => {
        const calculatedAttendance = [...streamAttendance]
        const threshold = 50


        const mergeSessions = ( sessions ) => {
            if ( !sessions?.length ) return []

            const sorted = [...sessions].sort( ( a, b ) => new Date( a.joined_at ) - new Date( b.joined_at ) )
            const merged = []
            let current = { ...sorted[0] }

            for ( let i = 1; i < sorted.length; i++ ) {
                const next = sorted[i]

                const currentEnd = new Date( current.left_at )
                const nextStart = new Date( next.joined_at )

                if ( nextStart <= currentEnd ) {
                    // merge if overlapping or touching
                    const nextEnd = new Date( next.left_at )
                    current.left_at = nextEnd > currentEnd ? next.left_at : current.left_at
                    current.duration = new Date( current.left_at ) - new Date( current.joined_at )
                } else {
                    merged.push( current )
                    current = { ...next }
                }
            }

            merged.push( current )
            return merged
        }

        const creatorSessions = mergeSessions( [...( creatorAttendanceData?.sessions || [] )].filter( ( { left_at, joined_at } ) => new Date( left_at ) > new Date( creatorAttendanceData?.meetingTime?.starts_at ) && new Date( joined_at ) < new Date( creatorAttendanceData?.meetingTime?.ends_at ) ) )

        // host's session boundaries
        let earliestSession = creatorSessions?.[0] || { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at }
        let latestSession = creatorSessions?.[creatorSessions.length - 1] || { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at }

        latestSession.left_at = new Date( Math.min( new Date( creatorAttendanceData?.meetingTime?.ends_at ), new Date( latestSession?.left_at ) ) )
        earliestSession.joined_at = new Date( Math.max( new Date( creatorAttendanceData?.meetingTime?.starts_at ), new Date( earliestSession?.joined_at ) ) )

        if ( earliestSession ) {
            earliestSession.joined_at = new Date( earliestSession.joined_at )
            earliestSession.left_at = new Date( earliestSession.left_at )
        }
        if ( latestSession ) {
            latestSession.joined_at = new Date( latestSession.joined_at )
            latestSession.left_at = new Date( latestSession.left_at )
        }


        // decide totalDuration
        let totalDuration = latestSession.left_at - earliestSession.joined_at
        if ( creatorSessions && creatorSessions.length > 0 )
            totalDuration = creatorSessions.reduce(
                ( acc, { duration } ) => acc + ( duration ),
                0
            )

        totalDuration = totalDuration || 10

        for ( const attendanceDetails of calculatedAttendance ) {
            let adjustedTotalTime = attendanceDetails.total_time

            if ( earliestSession && latestSession ) {

                // apply boundary trimming only if hostsDuration = true
                const adjustedSessions = []
                for ( const [idx, session] of mergeSessions( attendanceDetails.sessions || [] )?.entries() ) {
                    let { joined_at, left_at } = session
                    joined_at = new Date( joined_at )
                    left_at = new Date( left_at )

                    if ( left_at <= earliestSession.joined_at || joined_at >= latestSession.left_at ) {
                        continue
                    }
                    // Trim first session if before host's first joined_at
                    if ( idx === 0 && joined_at < earliestSession.joined_at ) {
                        joined_at = earliestSession.joined_at
                    }

                    // Trim last session if after host's last left_at
                    if ( idx === attendanceDetails.sessions.length - 1 && left_at > new Date( latestSession.left_at ) ) {
                        left_at = new Date( latestSession.left_at )
                    }

                    adjustedSessions.push( { joined_at, left_at } )
                }

                adjustedTotalTime = adjustedSessions.reduce(
                    ( acc, { joined_at, left_at } ) => acc + ( left_at - joined_at ),
                    0
                )
            }

            attendanceDetails.total_time = adjustedTotalTime

            attendanceDetails.present = adjustedTotalTime >= ( totalDuration * ( threshold / 100 ) )
            attendanceDetails.present_percentage = Math.min(
                Math.round( ( adjustedTotalTime * 100 ) / totalDuration ),
                100
            )
            // console.log( attendanceDetails )

            attendanceDetails.threshold_percent = threshold
            attendanceDetails.threshold_in_ms = totalDuration * ( threshold / 100 )
        }

        return sortArrayOfObjectsByKey( calculatedAttendance, 'total_time', -1, 'number' )
    }, [creatorAttendanceData, streamAttendance] )

    const fetchMeetingAttendance = useCallback( async () => {
        await OnlineSessionsStore.fetchOnlineSessionAttendance( meeting_id )
        setPresentUsersList( OnlineSessionsStore.getPresentUsersList )
    }, [meeting_id, OnlineSessionsStore] )

    useEffect( () => {
        fetchMeetingAttendance()
    }, [fetchMeetingAttendance] )



    return (
        <Dialog
            open={open} slotProps={{
                paper: { sx: { width: "90%", maxWidth: "1200px", display: "flex", flexDirection: "column" } }
            }}
        >
            <Box sx={{ zIndex: 10 }} position="sticky" top="0" bgcolor={palette.background.paper} display="flex" alignItems="center" flexWrap="wrap" justifyContent="space-between" padding="10px 20px" >
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Review and sync class attendance with ERP</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                    <IconButton onClick={() => handleClose()}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>

            <Box display="flex" flexDirection="column" overflow="auto" gap="10px" flexGrow={1} alignItems="center" flexWrap='wrap'>
                {(
                    calculatedAttendance.length > 0 ||
                    videoWatchAttendance.length > 0
                ) &&
                    <TableContainer className='custom-scrollbar' sx={{ border: border[1], borderRadius: "0", overflow: "auto", flexGrow: 1, maxHeight: "400px" }}>
                        <Table stickyHeader sx={{ bgcolor: palette.common.bg }}>
                            <TableHeader columns={columns} />
                            <TableBody>
                                {calculatedAttendance.map( ( user, index ) => {

                                    return (
                                        <AttendanceTableRow setPresentUsersList={setPresentUsersList} isPresent={presentUsersList.includes( user.user_id )} index={index + 1} key={user.user_id} user={user} />
                                    )
                                } )}

                                {videoWatchAttendance.map( ( viewer, index ) => {

                                    const didUserStreamClass = calculatedAttendance.find( u => u.user_id === viewer.user_id )

                                    if ( didUserStreamClass )
                                        return null

                                    return (
                                        <AttendanceTableRow sessionAttendance={false} setPresentUsersList={setPresentUsersList} isPresent={presentUsersList.includes( viewer.user_id )} index={calculatedAttendance.length + index + 1} key={viewer.user_id} user={viewer} />
                                    )
                                } )}
                            </TableBody>
                        </Table>
                    </TableContainer>}

                {
                    calculatedAttendance.length === 0 &&
                    videoWatchAttendance.length === 0 &&

                    <Box width="100%" padding="20px" display="flex" flexDirection="column" alignItems="center" gap="10px" borderRadius={layout.borderRadius} >
                        <Box component="img" width="200px" src={noDataImg} />
                        <Typography variant='subtitle2' component="p" textAlign="center">No users met the 50% attendance requirement. Remove the ‘Show Only Present’ filter to view all users.</Typography>
                    </Box>}

            </Box>
            {<Box alignItems="center" padding="10px 20px" bgcolor={palette.contentBg} display="flex" justifyContent="space-between">
                <Typography variant='subtitle2' color='textSecondary' component="h6">{presentUsersList.length} marked as present</Typography>
                <Box display="flex" alignItems="center" gap={layout.cardsGap}>
                    <Button color='greyed' size='small' onClick={handleClose}>Cancel</Button>
                    <Button variant='contained' size='small' disableElevation startIcon={Icons.default.CheckCircleIcon}>Sync Attendance</Button>
                </Box>
            </Box>}

        </Dialog>
    )
} )

export default HybridClassAttendanceConfirmDialog
